import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper } from '@mui/material';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import DayContentContainer from './DayContentContainer';
import DateContainer from './DateContainer';
import DayContainer from './DayContainer';
import WeekDayHeader from './WeekDayHeader';
import InvalidDay from './InvalidDay';
import PeriodNavigator from './PeriodNavigator';

dayjs.extend(isToday);

const DEFAULT_DATE_CONTAINER_OBJECT = {
  rightComponent: null,
  rightComponentProps: null,
};
const DEFAULT_DAY_CELL_CONTAINER_OBJECT = {
  component: null,
  componentProps: null,
};
const DEFAULT_NAVIGATION_CONTAINER_OBJECT = {
  rightComponent: null,
  rightComponentProps: null,
};

function periodGrid({ startCursor, days }) {
  const start = startCursor.startOf('day');
  const leadingEmpty = start.day();
  const dates = [];
  for (let i = 0; i < leadingEmpty; i += 1) {
    dates.push(null);
  }
  for (let d = 0; d < days; d += 1) {
    dates.push(start.add(d, 'day'));
  }
  while (dates.length % 7 !== 0) {
    dates.push(null);
  }
  return dates;
}

function resolveVisiblePeriod({ days, weeks, period }) {
  if (weeks != null) {
    const weekCount = Math.max(1, weeks);
    return {
      isWeekPeriod: true,
      weekCount,
      visibleDays: weekCount * 7,
    };
  }

  return {
    isWeekPeriod: false,
    weekCount: null,
    visibleDays: Math.max(1, days ?? period ?? 30),
  };
}

function PeriodCalendar({
  value = {},
  period,
  days,
  weeks,
  onChange,
  onDayClick = null,
  isClickable = () => false,
  DateContainerComponent = DEFAULT_DATE_CONTAINER_OBJECT,
  getDateCellValue = () => value,
  DayCellContentComponent = DEFAULT_DAY_CELL_CONTAINER_OBJECT,
  NavigationComponent = DEFAULT_NAVIGATION_CONTAINER_OBJECT,
}) {
  const { isWeekPeriod, weekCount, visibleDays } = resolveVisiblePeriod({
    days,
    weeks,
    period,
  });
  const [cursor, setCursor] = useState(() =>
    isWeekPeriod ? dayjs().startOf('week') : dayjs().startOf('day'),
  );

  const endCursor = useMemo(
    () => cursor.add(visibleDays - 1, 'day'),
    [cursor, visibleDays],
  );

  const cellItems = useMemo(() => {
    const raw = periodGrid({ startCursor: cursor, days: visibleDays });
    const periodLabel = `${cursor.format('YYYY-MM-DD')}_${visibleDays}`;
    return raw.map((cell, pos) => {
      const row = Math.floor(pos / 7);
      const col = pos % 7;
      return {
        textDate: cell ? cell.format('YYYY-MM-DD') : null,
        cellDate: cell,
        gridKey: cell
          ? cell.format('YYYY-MM-DD')
          : `placeholder-${periodLabel}-r${row}-c${col}`,
      };
    });
  }, [cursor, visibleDays]);

  const hidePrev = !cursor
    .startOf('day')
    .isAfter(dayjs().startOf('day'), 'day');

  const goPrev = () => {
    setCursor(c =>
      isWeekPeriod
        ? c.subtract(weekCount, 'week').startOf('week')
        : c.subtract(visibleDays, 'day'),
    );
  };

  const goNext = () => {
    setCursor(c =>
      isWeekPeriod
        ? c.add(weekCount, 'week').startOf('week')
        : c.add(visibleDays, 'day'),
    );
  };

  const currentDate = dayjs().startOf('day');

  useEffect(() => {
    onChange(cursor);
  }, [cursor]);

  const {
    rightComponent: RightComponentForNavigationContainer = null,
    rightComponentProps: NavigationContainerProps = {},
  } = NavigationComponent || DEFAULT_NAVIGATION_CONTAINER_OBJECT;

  const periodNavigator = (
    <PeriodNavigator
      hidePrev={hidePrev}
      goPrev={goPrev}
      cursor={cursor}
      endCursor={endCursor}
      goNext={goNext}
    />
  );

  return (
    <Box>
      <Paper sx={{ p: 1, mb: 2 }} elevation={0} variant="outlined">
        {RightComponentForNavigationContainer ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            {periodNavigator}
            <RightComponentForNavigationContainer
              {...NavigationContainerProps}
            />
          </Box>
        ) : (
          periodNavigator
        )}
      </Paper>
      <Paper sx={{ p: 1, mb: 2 }} elevation={0} variant="outlined">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
          }}>
          <WeekDayHeader />

          {cellItems.map(({ cellDate, gridKey, textDate }) => {
            if (!cellDate) {
              return <InvalidDay key={gridKey} />;
            }

            const isItToday = dayjs(cellDate).isToday();

            const {
              rightComponent: RightComponentForDateContainer = null,
              rightComponentProps = {},
            } = DateContainerComponent || DEFAULT_DATE_CONTAINER_OBJECT;

            const { component: DayCellComponent = null, componentProps = {} } =
              DayCellContentComponent || DEFAULT_DAY_CELL_CONTAINER_OBJECT;

            const cellDateValue = getDateCellValue({
              value,
              gridKey,
              cellDate,
              textDate,
              isToday: isItToday,
            });

            const isCellClickable =
              Boolean(onDayClick) &&
              isClickable({
                cellDate,
                gridKey,
                currentDate,
                isToday: isItToday,
                value,
                cellDateValue,
                textDate,
              });

            return (
              <DayContainer
                key={gridKey}
                cellDate={cellDate}
                {...(isCellClickable && {
                  onClick: () => {
                    onDayClick({
                      cellDate,
                      cellDateValue,
                      gridKey,
                      value,
                      textDate,
                      isToday: isItToday,
                      currentDate,
                    });
                  },
                })}
                isToday={isItToday}>
                <DateContainer cellDate={cellDate} isToday={isItToday}>
                  {DateContainerComponent?.rightComponent && (
                    <RightComponentForDateContainer
                      cellDate={cellDate}
                      gridKey={gridKey}
                      value={value}
                      cellDateValue={cellDateValue}
                      textDate={textDate}
                      isToday={isItToday}
                      currentDate={currentDate}
                      {...rightComponentProps}
                    />
                  )}
                </DateContainer>
                <DayContentContainer>
                  {DayCellContentComponent?.component && (
                    <DayCellComponent
                      cellDate={cellDate}
                      gridKey={gridKey}
                      value={value}
                      cellDateValue={cellDateValue}
                      textDate={textDate}
                      isToday={isItToday}
                      currentDate={currentDate}
                      {...componentProps}
                    />
                  )}
                </DayContentContainer>
              </DayContainer>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}

export default PeriodCalendar;
